"use client";

import React from "react";
import { Paintbrush, Hammer, Zap, Shovel, Briefcase, LayoutTemplate, Palette, ArrowRight, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AdvancedPackageFeatures() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const industries = [{
    icon: Paintbrush,
    label: "Painters",
    color: "text-pink-400"
  }, {
    icon: Zap,
    label: "Electricians",
    color: "text-yellow-400"
  }, {
    icon: Shovel,
    label: "Landscapers",
    color: "text-green-400"
  }, {
    icon: Hammer,
    label: "Builders",
    color: "text-blue-400"
  }];
  const deepDives = [{
    title: "1-Hour Branding Session",
    icon: Palette,
    description: "We sit down with you (virtually or in-person) to define your business's voice, colour palette, and visual identity before we write a single line of code.",
    whyItMatters: "Consistent, professional branding builds instant trust with homeowners and contractors. It separates you from the 'guy with a van' and positions you as a premium service provider."
  }, {
    title: "Strategic 5-Page Structure",
    icon: LayoutTemplate,
    description: "Beyond a simple landing page, we build a complete digital presence: Home, Services, About, Portfolio, and Contact. Each page is optimized for specific keywords.",
    whyItMatters: "More pages mean more opportunities to rank on Google. A dedicated 'Portfolio' page lets you showcase your best work, while a 'Services' page details exactly what you offer, reducing low-quality leads."
  }, {
    title: "Priority Support & Updates",
    icon: CircleCheck,
    description: "The Advanced package includes 5 content changes per month and unlimited technical calls. Need to update a price or add a new project photo? We handle it fast.",
    whyItMatters: "Your business evolves, and your website should too. Regular updates keep your site secure, fresh, and relevant without you needing to learn how to code."
  }];
  return <section id="advanced-package-features" className="bg-background text-foreground py-20 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          
          {}
          <div className="relative">
            <div className="sticky top-24">
              <div className="mb-8">
                <h2 className="text-3xl font-bold tracking-tight ![margin:-20px_0px_0px_0px] md:text-4xl">
                  Built for <span className="!text-primary">Trades</span> & <span className="!text-primary">Services</span>
                </h2>
                <p className="text-muted-foreground text-lg">
                  The Advanced Package is designed for growing businesses that need more than just a digital business card. It's for those ready to dominate their local market.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {industries.map((item, i) => <Card key={i} data-index={i} className="bg-card/50 border-border/50 backdrop-blur-sm hover:border-primary/50 transition-colors duration-300 group">
                    <CardContent className="p-6 flex flex-col items-center justify-center text-center gap-4 h-40">
                      <div className={`p-3 rounded-full bg-secondary group-hover:bg-primary/10 transition-colors duration-300`}>
                        <item.icon className={`w-10 h-10 ${item.color} group-hover:text-primary transition-colors duration-300`} />
                      </div>
                      <span className="font-semibold text-lg">{item.label}</span>
                    </CardContent>
                  </Card>)}
              </div>
              
              <div className="mt-8 p-6 bg-secondary/30 rounded-xl border border-border/50 backdrop-blur-sm">
                <div className="flex items-start gap-4">
                  <Briefcase className="w-6 h-6 text-primary shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Not listed here?</h4>
                    <p className="text-sm text-muted-foreground">
                      We work with all service-based businesses including cleaners, detailers, removalists, and more.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex flex-col gap-8">
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-foreground">Package Deep Dive</h3>
              <p className="text-muted-foreground">
                A closer look at the premium features included in the $1400 + GST package.
              </p>
            </div>

            <div className="flex flex-col gap-8">
              {deepDives.map((feature, i) => <div key={i} data-index={i} className="group">
                  <div className="flex gap-5 items-start">
                    <div className="shrink-0 mt-1">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 shadow-[0_0_15px_var(--color-primary)/10]">
                        <feature.icon className="w-6 h-6" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <h4 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                        {feature.title}
                      </h4>
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                      
                      <div className="bg-secondary/50 rounded-lg p-4 border-l-2 border-primary/50 mt-2">
                        <p className="text-sm font-medium text-primary mb-1 uppercase tracking-wider text-xs">
                          Why this matters
                        </p>
                        <p className="text-sm text-muted-foreground italic">
                          "{feature.whyItMatters}"
                        </p>
                      </div>
                    </div>
                  </div>
                  {i < deepDives.length - 1 && <Separator className="mt-8 bg-border/50" />}
                </div>)}
            </div>

            <div className="mt-4 pt-4">
              <Button asChild size="lg" className="w-full sm:w-auto rounded-full text-base font-semibold h-12 px-8 shadow-[0_0_20px_var(--color-primary)/30] hover:shadow-[0_0_30px_var(--color-primary)/50] transition-all duration-300">
                <Link to="/ContactPage">
                  Get Started with Advanced <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}